// frontend/src/features/livestock/AddTimelineEntry.tsx
/**
 * Inline form for adding a timeline entry with optional photo.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  Card,
  Textarea,
  Group,
  Button,
  Image,
  Text,
  Progress,
  Stack,
  Box,
  CloseButton,
  Modal,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { IconCamera } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useBlocker } from 'react-router-dom';
import { useTimeline } from '@/hooks/useTimeline';
import { LivestockPicker } from './LivestockPicker';
import { PhotoCropModal } from './PhotoCropModal';
import { captureDateToPrefill, readCaptureDate } from './photoMetadata';
import type { CaptureDate, CapturePrefill } from './photoMetadata';

interface AddTimelineEntryProps {
  livestockId: number;
  livestockType?: string;
  onEntryAdded?: () => void;
}

const PLACEHOLDER_BY_TYPE: Record<string, string> = {
  coral: "What's happening with this coral?",
  fish: "What's happening with this fish?",
  invertebrate: "What's happening with this invertebrate?",
  plant: "What's happening with this plant?",
};

interface EntryDateState {
  value: Date | null;
  /** Set while `value` is the attached photo's capture date and the user hasn't changed it. */
  fromPhoto: (CapturePrefill & { previous: Date | null }) | null;
}

type EntryDateAction =
  | { type: 'userChanged'; value: Date | null }
  | { type: 'photoAttached'; prefill: CapturePrefill | null }
  | { type: 'photoRemoved' }
  | { type: 'reset' };

function entryDateReducer(state: EntryDateState, action: EntryDateAction): EntryDateState {
  switch (action.type) {
    case 'userChanged':
      return { value: action.value, fromPhoto: null };
    case 'photoAttached': {
      // A new photo replaces any date the previous photo set, even when it has none itself.
      const previous = state.fromPhoto ? state.fromPhoto.previous : state.value;
      return action.prefill
        ? { value: action.prefill.date, fromPhoto: { ...action.prefill, previous } }
        : { value: previous, fromPhoto: null };
    }
    case 'photoRemoved':
      return state.fromPhoto ? { value: state.fromPhoto.previous, fromPhoto: null } : state;
    case 'reset':
      return { value: new Date(), fromPhoto: null };
  }
}

export function AddTimelineEntry({ livestockId, livestockType, onEntryAdded }: AddTimelineEntryProps) {
  const { addEntry, isCreating, uploadProgress } = useTimeline(livestockId);

  const [entryText, setEntryText] = useState('');
  const [{ value: entryDate, fromPhoto: dateFromPhoto }, dispatchEntryDate] = useReducer(
    entryDateReducer,
    { value: new Date(), fromPhoto: null },
  );
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [targetLivestockId, setTargetLivestockId] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  // Capture date of the file in the crop modal, read before cropping re-encodes it without EXIF
  const pendingCaptureRef = useRef<Promise<CaptureDate | null> | null>(null);
  // Bumped whenever the attached photo changes, so a late capture date read is dropped
  const attachCountRef = useRef(0);

  const hasContent = entryText.trim().length > 0 || selectedPhoto !== null;

  // Warn user about unsaved content when navigating away (React Router)
  const blocker = useBlocker(hasContent && !isCreating);

  // Warn user about unsaved content when closing the tab/browser
  const handleBeforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (hasContent) {
        e.preventDefault();
      }
    },
    [hasContent],
  );

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [handleBeforeUnload]);

  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Client-side validation with user feedback
    if (!file.type.startsWith('image/')) {
      notifications.show({
        title: 'Invalid file',
        message: 'Please select an image file (JPEG, PNG, or WebP)',
        color: 'red',
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      notifications.show({
        title: 'File too large',
        message: 'Photos must be 10MB or smaller',
        color: 'red',
      });
      return;
    }

    pendingCaptureRef.current = readCaptureDate(file).catch(() => null);

    // Open crop modal instead of directly setting the photo
    setCropImageUrl(URL.createObjectURL(file));
  };

  const attachPhoto = (file: File): void => {
    setSelectedPhoto(file);
    setPhotoPreviewUrl(URL.createObjectURL(file));

    const attach = ++attachCountRef.current;
    const capture = pendingCaptureRef.current ?? Promise.resolve(null);
    pendingCaptureRef.current = null;
    void capture.then((result) => {
      if (attach !== attachCountRef.current) return;
      dispatchEntryDate({ type: 'photoAttached', prefill: captureDateToPrefill(result, new Date()) });
    });
  };

  const handleRemovePhoto = () => {
    attachCountRef.current++;
    dispatchEntryDate({ type: 'photoRemoved' });
    setSelectedPhoto(null);
    setTargetLivestockId(null);
    if (photoPreviewUrl) {
      URL.revokeObjectURL(photoPreviewUrl);
      setPhotoPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCropConfirm = (croppedBlob: Blob) => {
    // Clean up the original object URL used for cropping
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropImageUrl(null);

    // Convert Blob to File (so FormData upload works the same)
    attachPhoto(new File([croppedBlob], 'cropped-photo.jpg', { type: 'image/jpeg' }));
  };

  const handleSkipCrop = (originalBlob: Blob) => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropImageUrl(null);

    attachPhoto(new File([originalBlob], 'full-photo.jpg', { type: originalBlob.type || 'image/jpeg' }));
  };

  const handleCropCancel = () => {
    if (cropImageUrl) {
      URL.revokeObjectURL(cropImageUrl);
    }
    setCropImageUrl(null);
    pendingCaptureRef.current = null;
    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!hasContent || !entryDate) return;

    try {
      await addEntry({
        entry_text: entryText.trim() || null,
        entry_date: entryDate.toISOString(),
        photo: selectedPhoto,
        target_livestock_id: targetLivestockId ?? undefined,
        taken_at_source: dateFromPhoto?.source,
      });

      // Reset form
      setEntryText('');
      dispatchEntryDate({ type: 'reset' });
      setTargetLivestockId(null);
      handleRemovePhoto();
      onEntryAdded?.();
    } catch {
      // Error notification handled by hook
    }
  };

  return (
    <Card withBorder radius="xl" p={{ base: 'sm', md: 'lg' }}>
      <Stack gap="sm">
        <Text
          component="h3"
          fw={600}
          size="sm"
          style={{ fontFamily: 'Space Grotesk, sans-serif', margin: 0 }}
        >
          Add Note
        </Text>

        <Textarea
          placeholder={PLACEHOLDER_BY_TYPE[livestockType ?? ''] ?? 'Add a note about this organism...'}
          aria-label="Timeline entry note"
          value={entryText}
          onChange={(e) => setEntryText(e.currentTarget.value)}
          minRows={2}
          maxRows={6}
          autosize
          disabled={isCreating}
        />

        {/* Photo preview */}
        {photoPreviewUrl && (
          <Box
            style={{
              position: 'relative',
              borderRadius: 'var(--mantine-radius-md)',
              overflow: 'visible',
              width: 120,
              height: 120,
            }}
          >
            <Image
              src={photoPreviewUrl}
              alt="Photo preview"
              w={120}
              h={120}
              fit="cover"
              radius="md"
            />
            <CloseButton
              aria-label="Remove photo"
              onClick={handleRemovePhoto}
              size="xs"
              variant="filled"
              color="dark"
              radius="xl"
              style={{
                position: 'absolute',
                top: -6,
                right: -6,
              }}
            />
          </Box>
        )}

        {/* Livestock re-targeting (only when photo is selected) */}
        {selectedPhoto && (
          <LivestockPicker
            value={targetLivestockId ?? livestockId}
            onChange={setTargetLivestockId}
          />
        )}

        {/* Upload progress */}
        {uploadProgress !== null && (
          <Progress
            value={uploadProgress}
            size="sm"
            radius="xl"
            color="teal"
            animated={uploadProgress < 100}
          />
        )}

        {/* Action row (top-aligned so the date hint under the picker doesn't shift the buttons) */}
        <Group justify="space-between" align="flex-start">
          <Group gap="xs" align="flex-start">
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handlePhotoSelect}
              style={{ display: 'none' }}
            />

            <Button
              variant="subtle"
              size="xs"
              leftSection={<IconCamera size={16} />}
              onClick={() => fileInputRef.current?.click()}
              disabled={isCreating}
              aria-label="Add photo"
            >
              Add Photo
            </Button>

            <DateTimePicker
              value={entryDate}
              onChange={(val: string | Date | null) =>
                dispatchEntryDate({
                  type: 'userChanged',
                  // dayjs, not new Date: older WebKit can't parse "YYYY-MM-DD HH:mm:ss".
                  value: val ? (typeof val === 'string' ? dayjs(val).toDate() : val) : null,
                })
              }
              size="xs"
              style={{ maxWidth: 200 }}
              disabled={isCreating}
              aria-label="Entry date"
              // Caption size (12 px); Mantine's own description would be 10 px at size xs.
              // Inside the description slot so the picker's aria-describedby still points at it.
              description={
                dateFromPhoto ? (
                  <Text span size="xs" c="gray.7">
                    Date from photo
                  </Text>
                ) : undefined
              }
              inputWrapperOrder={['label', 'input', 'description', 'error']}
            />
          </Group>

          <Button
            size="xs"
            onClick={handleSubmit}
            disabled={!hasContent || isCreating}
            loading={isCreating}
            aria-label="Save"
          >
            Save
          </Button>
        </Group>
      </Stack>

      {/* Photo crop modal */}
      <PhotoCropModal
        opened={!!cropImageUrl}
        imageUrl={cropImageUrl ?? ''}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
        onSkipCrop={handleSkipCrop}
      />

      {/* Unsaved changes confirmation modal */}
      <Modal
        opened={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        title="Unsaved changes"
        centered
        size="sm"
      >
        <Text size="sm" mb="lg">
          You have an unsaved {selectedPhoto ? 'photo' : 'note'}. If you navigate away, it will be lost.
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => blocker.reset?.()}>
            Stay
          </Button>
          <Button color="red" onClick={() => blocker.proceed?.()}>
            Discard & leave
          </Button>
        </Group>
      </Modal>
    </Card>
  );
}
